import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';

import { ErrorMessage, Loading, withValidPermission } from '@Components';
import { useFetchData } from '@CustomHooks';
import { Permission, TaskType } from '@Models';
import { bopsApi } from '@Network';
import ScheduleConfiguration from './scheduleConfiguration';
import ScheduleDetail from './scheduleDetail';

interface ScheduleDetailContainerProps {
  scheduleId: string;
  view?: 'configuration';
}

const ScheduleDetailContainer = ({ scheduleId, view }: ScheduleDetailContainerProps) => {
  const { t } = useTranslation();

  const fetchDetail = useCallback(() => bopsApi.getScheduleDetail(scheduleId), [scheduleId]);
  const {
    data: detail,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch: refetchDetail,
  } = useFetchData(fetchDetail);

  const { data: taskTypes, isLoading: isLoadingTaskTypes, error: taskTypesError } = useFetchData(bopsApi.getTaskTypes);

  const taskTypesById = useMemo(() => {
    const byId: Record<string, TaskType> = {};
    (taskTypes ?? []).forEach((taskType) => {
      byId[taskType.id] = taskType;
    });
    return byId;
  }, [taskTypes]);

  useEffect(() => {
    if (detailError && detail) showErrorToast(t('Schedule.RefreshError'));
  }, [detail, detailError, t]);

  if ((isLoadingDetail && !detail) || isLoadingTaskTypes) {
    return <Loading title={t('Schedule.Detail.Loading')} />;
  }

  if (!detail || !taskTypes) {
    return (
      <ErrorMessage
        internalMessage={`Unable to get schedule: ${String(detailError ?? taskTypesError ?? 'not found')}`}
      />
    );
  }

  if (view === 'configuration') {
    return <ScheduleConfiguration detail={detail} taskTypes={taskTypes} onScheduleChanged={() => refetchDetail()} />;
  }

  return (
    <ScheduleDetail
      detail={detail}
      taskTypes={taskTypes}
      taskTypesById={taskTypesById}
      onScheduleChanged={() => refetchDetail()}
    />
  );
};

export default withValidPermission(ScheduleDetailContainer, [Permission.ViewSchedules, Permission.ManageSchedules]);
